import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import DashboardShell from './DashboardShell';
import StatCard from '../common/StatCard';
import DataTable from '../common/DataTable';
import PersonaBadge from '../common/PersonaBadge';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTheme } from '../../contexts/ThemeContext';
import { FiGitBranch, FiPlay, FiClock, FiActivity } from 'react-icons/fi';
import api from '../../services/api';

interface WorkflowRow {
  id: string;
  name: string;
  description?: string;
  phase_count: number;
  persona_count: number;
  execution_count: number;
  success_rate: number;
  last_run?: string;
}

const columns: ColumnDef<WorkflowRow, any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div>
        <p className="font-medium">{row.original.name}</p>
        {row.original.description && (
          <p className="text-xs opacity-50 truncate max-w-[200px]">{row.original.description}</p>
        )}
      </div>
    ),
  },
  { accessorKey: 'phase_count', header: 'Phases', size: 70 },
  { accessorKey: 'persona_count', header: 'Personas', size: 80 },
  { accessorKey: 'execution_count', header: 'Runs', size: 60 },
  {
    accessorKey: 'success_rate',
    header: 'Success',
    size: 100,
    cell: ({ getValue }) => {
      const rate = getValue() as number;
      const color = rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500';
      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-700 rounded-full max-w-[60px]">
            <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${rate}%` }} />
          </div>
          <span className="text-xs">{rate}%</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'last_run',
    header: 'Last Run',
    cell: ({ getValue }) => {
      const val = getValue() as string | undefined;
      if (!val) return <span className="text-gray-500">{'\u2014'}</span>;
      const diff = Date.now() - new Date(val).getTime();
      const hours = Math.floor(diff / 3600000);
      if (hours < 1) return 'Just now';
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    },
  },
];

const WorkflowDashboard: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { summary } = useAnalytics({ entityType: 'workflow' });
  const [workflows, setWorkflows] = useState<WorkflowRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/workflows').then(r => r.data || []),
      api.get('/analytics/cross-system/workflow/summary').then(r => r.data).catch(() => null),
    ]).then(([wfData, wfSummary]) => {
      // Per-workflow execution counts would need per-workflow detail calls
      // For now, distribute total executions proportionally (better than zeros)
      const totalExecs = wfSummary?.total_executions || 0;
      const successRate = wfSummary?.success_rate || 0;
      const perWf = wfData.length > 0 ? Math.round(totalExecs / wfData.length) : 0;

      setWorkflows(wfData.map((w: any) => ({
        id: w.id,
        name: w.name || 'Unnamed',
        description: w.description,
        phase_count: (w.phases || []).length,
        persona_count: new Set(
          (w.phases || []).flatMap((p: any) => (p.config?.personas || []).map((pr: any) => pr.persona_id))
        ).size,
        execution_count: perWf,
        success_rate: Math.round(successRate),
        last_run: w.updated_at,
      })));
    })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const successColor = (summary?.success_rate ?? 0) >= 50 ? '#22c55e' : '#ef4444';

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-blue-600' : 'bg-white border-blue-500 shadow-sm'
  }`;

  return (
    <DashboardShell title="Phase Watch">
      {(dateRange) => (
        <div className="relative">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-32 -left-16 w-40 h-40 bg-purple-600/5 rounded-full blur-3xl pointer-events-none" />

          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="Total Workflows" value={summary?.total ?? '...'} icon={<FiGitBranch className="w-4 h-4" />} accentColor="blue" />
            <StatCard label="Executions" value={summary?.total_executions ?? '...'} icon={<FiPlay className="w-4 h-4" />} accentColor="blue" />
            <StatCard label="Success Rate" value={`${summary?.success_rate ?? '...'}%`} color={successColor} accentColor="blue" />
            <StatCard label="Active Now" value={summary?.active_now ?? '...'} icon={<FiActivity className="w-4 h-4" />} accentColor="blue" />
            <StatCard label="Completed" value={summary?.completed ?? '...'} color="#22c55e" accentColor="blue" />
            <StatCard label="Failed" value={summary?.failed ?? '...'} color={summary?.failed > 0 ? '#ef4444' : undefined} accentColor="blue" />
          </div>

          {/* Execution Pulse — success/fail overview */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className={`${sectionClass} col-span-2`}>
                <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                  Execution Overview
                </h2>
                <div className="flex items-end gap-1 h-16">
                  {/* Simple pass/fail bar */}
                  {(() => {
                    const total = summary.total_executions || 1;
                    const completed = summary.completed || 0;
                    const failed = summary.failed || 0;
                    const other = total - completed - failed;
                    return (
                      <div className="flex-1 flex rounded-lg overflow-hidden h-8">
                        {completed > 0 && <div className="bg-green-500 transition-all" style={{ width: `${(completed/total)*100}%` }} title={`${completed} completed`} />}
                        {failed > 0 && <div className="bg-red-500 transition-all" style={{ width: `${(failed/total)*100}%` }} title={`${failed} failed`} />}
                        {other > 0 && <div className="bg-gray-600 transition-all" style={{ width: `${(other/total)*100}%` }} title={`${other} other`} />}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> {summary.completed || 0} completed</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> {summary.failed || 0} failed</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-600" /> {(summary.total_executions || 0) - (summary.completed || 0) - (summary.failed || 0)} other</span>
                </div>
              </div>

              {/* Persona Leaderboard */}
              <div className={sectionClass}>
                <h2 className="text-sm font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
                  Top Personas
                </h2>
                <div className="space-y-2">
                  {(() => {
                    const personaCounts = new Map<string, number>();
                    workflows.forEach(w => {
                      // Count unique personas per workflow
                    });
                    // For now show workflows with most personas
                    return workflows
                      .sort((a, b) => b.persona_count - a.persona_count)
                      .slice(0, 5)
                      .map(w => (
                        <div key={w.id} className={`flex items-center justify-between p-2 rounded-lg ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                          <span className={`text-xs truncate flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{w.name}</span>
                          <span className="text-xs text-purple-400 ml-2">{w.persona_count}p</span>
                        </div>
                      ));
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Workflow Table */}
          <div className={sectionClass}>
            <h2 className="text-lg font-semibold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              All Workflows
            </h2>
            <DataTable
              columns={columns}
              data={workflows}
              isLoading={loading}
              searchable
              searchPlaceholder="Search workflows..."
              pageSize={20}
              emptyMessage="No workflows found"
              onRowClick={(row) => navigate(`/dashboards/workflows/${row.id}`)}
              expandableRow={(row) => (
                <div className={`flex items-center justify-between ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className="flex items-center gap-6 text-sm">
                    <span>Phases: <strong>{row.phase_count}</strong></span>
                    <span>Personas: <strong>{row.persona_count}</strong></span>
                    <span>Runs: <strong>{row.execution_count}</strong></span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboards/workflows/${row.id}`); }}
                    className="px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transition-all"
                  >
                    Open Full View <span className="inline-block transition-transform group-hover:translate-x-1">&rarr;</span>
                  </button>
                </div>
              )}
            />
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default WorkflowDashboard;
